import type { PortfolioConversionBand } from '~/stores/mainStore/types';

export type PortfolioConversionDisplayState =
  | 'ahead'
  | 'behind'
  | 'no-returns'
  | 'no-history'
  | 'no-history-15'
  | 'no-history-45';

export type PortfolioConversionStatusIcon =
  | 'trophy'
  | 'chart-bar'
  | 'trend-up'
  | 'rocket-launch'
  | 'star'
  | 'flag';

export type PortfolioConversionTone = 'green' | 'blue' | 'red' | 'neutral';

export const PORTFOLIO_CONVERSION_TONES: Record<
  PortfolioConversionTone,
  {
    accent: string;
    fill: string;
    percentageClass: string;
    pillClass: string;
    statusClass: string;
    ytdClass: string;
  }
> = {
  green: {
    accent: '#0e9f6e',
    fill: '#0e9f6e',
    percentageClass: 'text-[#0e9f6e]',
    pillClass: 'bg-emerald-50 text-[#0e9f6e] dark:bg-emerald-950/40',
    statusClass: 'text-[#0e9f6e]',
    ytdClass: 'text-[#0e9f6e]',
  },
  blue: {
    accent: '#00A2E1',
    fill: '#00A2E1',
    percentageClass: 'text-slate-900 dark:text-white',
    pillClass: 'bg-slate-100 text-slate-900 dark:bg-gray-700 dark:text-gray-100',
    statusClass: 'text-slate-900 dark:text-gray-100',
    ytdClass: 'text-[#00A2E1]',
  },
  red: {
    accent: '#f05252',
    fill: '#f05252',
    percentageClass: 'text-[#f05252]',
    pillClass: 'bg-red-50 text-[#f05252] dark:bg-red-950/40',
    statusClass: 'text-[#3B4143] dark:text-gray-100',
    ytdClass: 'text-[#f05252]',
  },
  neutral: {
    accent: '#64748b',
    fill: '#64748b',
    percentageClass: 'text-slate-900 dark:text-white',
    pillClass: 'bg-slate-100 text-slate-900 dark:bg-gray-700 dark:text-gray-100',
    statusClass: 'text-slate-900 dark:text-gray-100',
    ytdClass: 'text-slate-900 dark:text-gray-100',
  },
};

export function clampPortfolioConversionPosition(
  percentage: number | null | undefined,
): number {
  if (percentage === null || percentage === undefined) {
    return 0;
  }

  return Math.min(100, Math.max(0, percentage));
}

export function getPortfolioConversionState(input: {
  formsCount: number;
  percentage: number | null;
  previousPercentage: number | null;
  band: PortfolioConversionBand;
  livePropertiesCount: number;
}): PortfolioConversionDisplayState {
  if (input.livePropertiesCount === 0) {
    return 'no-returns';
  }

  if (input.previousPercentage === null) {
    if (input.band === 'red') {
      return 'no-history-15';
    }

    if (input.band === 'blue') {
      return 'no-history-45';
    }

    return 'no-history';
  }

  const current = input.percentage ?? 0;

  return current >= input.previousPercentage ? 'ahead' : 'behind';
}

export function getOrdersToMatchLastYear(
  formsCount: number,
  previousPercentage: number | null,
  livePropertiesCount: number,
): number {
  if (previousPercentage === null || livePropertiesCount <= 0) {
    return 0;
  }

  const target = Math.ceil((previousPercentage / 100) * livePropertiesCount);

  return Math.max(0, target - formsCount);
}

export function getPortfolioConversionCountLabel(
  formsCount: number,
  livePropertiesCount: number,
): string {
  const ordersLabel = formsCount === 1 ? 'order' : 'orders';
  const propertiesLabel = livePropertiesCount === 1 ? 'property' : 'properties';

  return `${formsCount.toLocaleString()} ${ordersLabel} from ${livePropertiesCount.toLocaleString()} ${propertiesLabel}`;
}

export function getPortfolioConversionYtdLabel(
  state: PortfolioConversionDisplayState,
  formsCount: number,
): string {
  if (state === 'no-returns') {
    return 'YTD · 0%';
  }

  return `YTD · ${formsCount.toLocaleString()}`;
}

export function getPortfolioConversionCopy(
  state: PortfolioConversionDisplayState,
  ordersToMatchLastYear = 0,
): {
  pill: string;
  status: string;
  icon: PortfolioConversionStatusIcon;
} {
  switch (state) {
    case 'ahead':
      return {
        pill: "You're ahead of last year",
        status: 'Great progress keep it going',
        icon: 'trophy',
      };
    case 'behind':
      return {
        pill: `${ordersToMatchLastYear.toLocaleString()} more ${
          ordersToMatchLastYear === 1 ? 'order' : 'orders'
        } will match last year`,
        status: "Keep building you're within reach",
        icon: 'chart-bar',
      };
    case 'no-returns':
      return {
        pill: 'Ready when you are',
        status: 'Your first return gets you moving',
        icon: 'flag',
      };
    case 'no-history-15':
      return {
        pill: 'Getting started',
        status: 'Every order moves you forward',
        icon: 'rocket-launch',
      };
    case 'no-history-45':
      return {
        pill: 'Building momentum',
        status: 'Good progress keep it going',
        icon: 'star',
      };
    case 'no-history':
    default:
      return {
        pill: 'Strong start this season',
        status: 'Good progress keep it going',
        icon: 'star',
      };
  }
}

export function getPortfolioConversionTone(
  band: PortfolioConversionBand,
  state?: PortfolioConversionDisplayState,
): PortfolioConversionTone {
  if (state === 'behind' && band === 'blue') {
    return 'neutral';
  }

  switch (band) {
    case 'red':
      return 'red';
    case 'blue':
      return 'blue';
    case 'green':
      return 'green';
    case 'not_scored':
    default:
      return 'neutral';
  }
}

export function showsGhostMarker(
  previousPercentage: number | null | undefined,
): boolean {
  return previousPercentage !== null && previousPercentage !== undefined;
}

export function showsCurrentCap(
  state: PortfolioConversionDisplayState,
): boolean {
  return (
    state === 'no-history' ||
    state === 'no-history-15' ||
    state === 'no-history-45'
  );
}

export function showsAheadEndCap(
  state: PortfolioConversionDisplayState,
): boolean {
  return state === 'ahead';
}

export function showsSolidGhost(
  state: PortfolioConversionDisplayState,
): boolean {
  return state === 'no-returns';
}

export function showsBehindMarker(
  state: PortfolioConversionDisplayState,
): boolean {
  return state === 'behind';
}
